// AuthContext.jsx
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import authService, { type ZybookUser } from '../services/zybook-auth-service';

const AuthContext = createContext<ZybookAuthContext | undefined>(undefined);

interface ZybookAuthContext {
    user: ZybookUser | null;
    authToken: string | null;
    loading: boolean;
    isAuthenticated: boolean;
    signIn: (email: string, password: string) => Promise<{ success: boolean; error?: any }>;
    signOut: () => void;
}

export function ZybookAuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<ZybookUser | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function prepareSession() {
            try {
                await authService.hydrateMaPermissionsIfMissing();
                if (authService.isAuthenticated()) {
                    setUser(authService.getUser());
                }
            } finally {
                setLoading(false);
            }
        }

        prepareSession(); // eslint-disable-line react-you-might-not-need-an-effect/no-initialize-state -- TODO: Rewrite this so we don't need to disable this rule
    }, []);

    const signIn = async (email: string, password: string) => {
        try {
            const result = await authService.signIn(email, password);
            setUser(result.user);
            return { success: true };
        } catch (error: any) {
            return {
                success: false,
                error,
            };
        }
    };

    const signOut = () => {
        authService.signOut();
        setUser(null);
    };

    const getAuthToken = () => {
        return authService.getAuthToken();
    };

    const value = {
        user,
        loading,
        authToken: getAuthToken(),
        isAuthenticated: authService.isAuthenticated(),
        signIn,
        signOut,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useZybookAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useZybookAuth must be used within ZybookAuthProvider');
    }
    return context;
};
